import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./components/ui/sheet";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Switch } from "./components/ui/switch";
import { Checkbox } from "./components/ui/checkbox";
import { ShoppingCart, Menu, X, Plus, Minus, Trash2, MapPin, Truck, ChevronRight, Phone, Clock, Lock, LogOut, Edit, Package, Settings, Image as ImageIcon, Users, Check, CreditCard, Banknote, FileText, Download, Bell, Send, MessageCircle, Instagram, Facebook, ChevronLeft } from "lucide-react";
import { initGA, trackPageView, trackAddToCart, trackBeginCheckout, trackPurchase, trackViewItem } from "./lib/gtag";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = BACKEND_URL || "https://granatus.eu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_granatus-bakery/artifacts/etdjs5eg_Granatus_logo_600px.png";

// SEO Component - updates document head with meta tags
const SEOHead = ({ title, description, image, keywords }) => {
  useEffect(() => {
    const siteTitle = "Granatus Bakery & Cafe";
    const pageTitle = title ? title + " | " + siteTitle : siteTitle;
    const pageDescription = description || "Занаятчийска пекарна и кафене в София. Пресни печива, торти и сладкиши всеки ден.";
    const pageImage = image || LOGO_URL;
    const pageUrl = window.location.href;
    
    // Update title
    document.title = pageTitle;
    
    // Helper function to update or create meta tag
    const updateMeta = (selector, content, attribute = "content") => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        if (selector.includes("property=")) {
          meta.setAttribute("property", selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes("name=")) {
          meta.setAttribute("name", selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute(attribute, content);
    };
    
    // Basic meta tags
    updateMeta('meta[name="description"]', pageDescription);
    if (keywords) {
      updateMeta('meta[name="keywords"]', keywords);
    }
    
    // Open Graph tags (Facebook, LinkedIn)
    updateMeta('meta[property="og:title"]', pageTitle);
    updateMeta('meta[property="og:description"]', pageDescription);
    updateMeta('meta[property="og:image"]', pageImage);
    updateMeta('meta[property="og:url"]', pageUrl);
    updateMeta('meta[property="og:type"]', "website");
    updateMeta('meta[property="og:locale"]', "bg_BG");
    updateMeta('meta[property="og:site_name"]', siteTitle);
    
    // Twitter Card tags
    updateMeta('meta[name="twitter:card"]', "summary_large_image");
    updateMeta('meta[name="twitter:title"]', pageTitle);
    updateMeta('meta[name="twitter:description"]', pageDescription);
    updateMeta('meta[name="twitter:image"]', pageImage);
    
  }, [title, description, image, keywords]);
  
  return null;
};

// Bulgarian translations
const translations = {
  nav: {
    home: "Начало",
    store: "Магазин",
    catering: "Кетъринг",
  },
  hero: {
    title1: "Занаятчийски печива",
    title2: "Пресни всеки ден",
    subtitle: "От нашите пещи до вашата маса. Изпитайте топлината на ръчно приготвени сладкиши, торти и ароматни печива, както и подбрани замразени продукти, създадени с любов в Granatus Bakery & Cafe.",
    shopNow: "Пазарувай сега",
    catering: "Кетъринг услуги",
  },
  store: {
    title: "Нашият магазин",
    subtitle: "Прясно изпечени продукти с любов",
    orderType: "Как искате поръчката си?",
    chooseOption: "Изберете взимане или доставка",
    pickup: "Взимане",
    delivery: "Доставка",
    add: "Добави",
    featured: "Препоръчано",
    categories: {
      All: "Всички",
      Bread: "Хляб",
      Pastries: "Сладкиши",
      Cakes: "Торти",
      Beverages: "Напитки",
    },
  },
  cart: {
    title: "Вашата кошница",
    orderType: "Тип поръчка",
    empty: "Кошницата ви е празна",
    continueShopping: "Продължи пазаруването",
    subtotal: "Междинна сума",
    checkout: "Към плащане",
    pickupTime: "Готова за взимане след 30-45 минути",
    deliveryTime: "Доставка в рамките на 1-2 часа",
  },
  checkout: {
    title: "Завършване на поръчката",
    customerInfo: "Информация за клиента",
    name: "Име и фамилия *",
    phone: "Телефон *",
    deliveryAddress: "Адрес за доставка *",
    pickupNote: "Ще ви очакваме на адрес: ул. Искър 33, София",
    notes: "Бележки към поръчката",
    notesPlaceholder: "Специални изисквания, алергии...",
    placeOrder: "Потвърди поръчката",
    placing: "Изпращане...",
    success: "Поръчката е приета! Ще се свържем с вас за потвърждение.",
    error: "Грешка при изпращане. Моля, опитайте отново.",
    total: "Общо",
    paymentMethod: "Начин на плащане",
    cash: "В брой",
    card: "С карта",
    cashNote: "Плащате при получаване",
    cardNote: "Сигурно онлайн плащане",
    processing: "Обработка на плащането...",
    paymentSuccess: "Плащането е успешно!",
    paymentError: "Грешка при плащането. Моля, опитайте отново.",
    agreeTerms: "Съгласен/а съм с",
    termsLink: "Общите условия",
    termsRequired: "Моля, приемете общите условия",
  },
  catering: {
    title: "Кетъринг услуги",
    subtitle: "Направете вашето събитие незабравимо с нашите занаятчийски печива и професионален кетъринг.",
    packages: "Нашите пакети",
    perPerson: "/човек",
    minGuests: "Минимум гости",
    includes: "Включва:",
    requestQuote: "Заявка за оферта",
    selectedPackage: "Избрали сте:",
    customNeeds: "Изберете пакет или ни разкажете за вашите нужди",
    form: {
      name: "Име и фамилия *",
      email: "Имейл *",
      phone: "Телефон *",
      date: "Дата на събитието *",
      guests: "Брой гости *",
      message: "Разкажете ни за събитието *",
      messagePlaceholder: "Тип събитие, диетични изисквания, специални заявки...",
      submit: "Изпрати заявка",
      submitting: "Изпращане...",
      success: "Заявката е изпратена! Ще се свържем с вас скоро.",
      error: "Грешка при изпращане. Моля, опитайте отново.",
    },
  },
  featured: {
    title: "Нашите фаворити",
    subtitle: "Подбрани селекции, обичани от нашите клиенти",
    viewAll: "Виж всички продукти",
  },
  philosophy: {
    title: "Направено с",
    titleAccent: "Страст",
    text1: "В Granatus вярваме, че всяка хапка разказва история. Нашите пекари пристигат преди зазоряване, за да създадат хлябове с хрупкави корички и мека сърцевина, сладкиши, които се разпадат при първото докосване, и торти, които празнуват най-сладките моменти в живота.",
    text2: "Използвайки само най-добрите съставки и изпитани във времето техники, ние изработваме всеки продукт с грижата, която вашето семейство заслужава.",
    years: "Години опит",
    recipes: "Уникални рецепти",
    fresh: "Пресни всеки ден",
  },
  cateringTeaser: {
    title: "Кетъринг за вашите специални моменти",
    subtitle: "Създаваме вкусове и преживявания, които допълват всеки повод – от лични празници до корпоративни събития. Предлагаме внимателно подбрано меню, качествени продукти и безупречно обслужване, за да се насладите на момента без компромиси.",
    explore: "Разгледай кетъринг",
  },
  footer: {
    description: "Занаятчийска пекарна и кафене, сервиращи пресни печива всеки ден. От нашите пещи до вашата маса с любов.",
    contact: "Контакт",
    hours: "Работно време",
    rights: "Всички права запазени.",
  },
  admin: {
    login: "Вход в админ панел",
    username: "Потребителско име",
    password: "Парола",
    loginBtn: "Вход",
    logout: "Изход",
    dashboard: "Табло",
    products: "Продукти",
    categories: "Категории",
    cateringPkg: "Кетъринг пакети",
    inquiries: "Заявки",
    addProduct: "Добави продукт",
    editProduct: "Редактирай продукт",
    deleteProduct: "Изтрий продукт",
    productName: "Име на продукта",
    productDesc: "Описание",
    productPrice: "Цена (лв)",
    productCategory: "Категория",
    productImage: "Снимка URL",
    productFeatured: "Препоръчан",
    productAvailable: "Наличен",
    save: "Запази",
    cancel: "Отказ",
    confirmDelete: "Сигурни ли сте, че искате да изтриете?",
    yes: "Да",
    no: "Не",
    noProducts: "Няма продукти",
    noPackages: "Няма пакети",
    noInquiries: "Няма заявки",
  },
  terms: {
    title: "Общи условия",
    lastUpdated: "Последна актуализация",
    backToHome: "Към началната страница",
  },
  settings: {
    title: "Настройки на сайта",
    siteTexts: "Текстове на сайта",
    notifications: "Известия",
    export: "Експорт",
    exportOrders: "Експортирай поръчки",
    exportDesc: "Изтеглете всички поръчки като CSV файл",
    telegramSettings: "Telegram настройки",
    viberSettings: "Viber настройки",
    enabled: "Активирано",
    botToken: "Bot Token",
    chatId: "Chat ID",
    userId: "User ID",
    phone: "Телефон",
    testNotification: "Тествай известия",
    testing: "Тестване...",
    testSuccess: "Тестът е успешен!",
    testFailed: "Тестът не успя",
    viberNote: "Забележка: За Viber известия потребителят трябва първо да започне чат с бота.",
    telegramNote: "Създайте бот чрез @BotFather в Telegram и вземете Chat ID от @userinfobot",
    heroSection: "Hero секция",
    heroTitle1: "Заглавие 1",
    heroTitle2: "Заглавие 2 (цветно)",
    heroSubtitle: "Подзаглавие",
    philosophySection: "Секция Философия",
    philosophyTitle: "Заглавие",
    philosophyTitleAccent: "Акцент (цветно)",
    philosophyText1: "Текст 1",
    philosophyText2: "Текст 2",
    footerSection: "Footer секция",
    footerDescription: "Описание",
    footerAddress: "Адрес",
    footerPhone: "Телефон",
    footerHours: "Работно време",
    socialSection: "Социални мрежи",
    instagramUrl: "Instagram URL",
    facebookUrl: "Facebook URL",
    termsSection: "Общи условия",
    termsContent: "Съдържание на общи условия",
    privacyContent: "Политика за поверителност",
    seoSection: "SEO Оптимизация",
    seoTitle: "Заглавие на сайта (Title)",
    seoDescription: "Мета описание",
    seoKeywords: "Ключови думи",
    seoOgImage: "Снимка за споделяне (Open Graph)",
    save: "Запази настройките",
    saving: "Запазване...",
    saved: "Настройките са запазени!",
  },
};

// Order status options for admin panel
const ORDER_STATUS_OPTIONS = [
  { value: "pending", label: "Чакаща", color: "bg-yellow-500" },
  { value: "confirmed", label: "Потвърдена", color: "bg-blue-500" },
  { value: "preparing", label: "Приготвя се", color: "bg-purple-500" },
  { value: "ready", label: "Готова", color: "bg-green-500" },
  { value: "delivering", label: "Доставя се", color: "bg-cyan-500" },
  { value: "completed", label: "Завършена", color: "bg-emerald-600" },
  { value: "cancelled", label: "Отменена", color: "bg-red-500" },
];

// Generate session ID for cart
const getSessionId = () => {
  let sessionId = localStorage.getItem("granatus_session");
  if (!sessionId) {
    sessionId = "session_" + Math.random().toString(36).substring(2, 15);
    localStorage.setItem("granatus_session", sessionId);
  }
  return sessionId;
};

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], delivery_type: "pickup" });
  const [loading, setLoading] = useState(false);
  const sessionId = getSessionId();

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  const addToCart = async (productId, quantity = 1) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/cart/add`, {
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
      setCart(response.data);
      toast.success("Добавено в кошницата!");
      // GA4: add_to_cart event. Resolve product details from the updated cart.
      const item = (response.data?.items || []).find((i) => i.product_id === productId);
      if (item) {
        trackAddToCart(
          { id: item.product_id, name: item.name, price: item.price, category: item.category },
          quantity
        );
      }
    } catch (error) {
      toast.error("Грешка при добавяне");
    }
    setLoading(false);
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      const response = await axios.put(`${API}/cart/update`, {
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
      setCart(response.data);
    } catch (error) {
      toast.error("Грешка при обновяване");
    }
  };

  const removeItem = async (productId, silent = false) => {
    try {
      const response = await axios.delete(`${API}/cart/${sessionId}/item/${productId}`);
      setCart(response.data);
      if (!silent) {
        toast.success("Продуктът е премахнат");
      }
    } catch (error) {
      if (!silent) {
        toast.error("Грешка при премахване");
      }
    }
  };

  const setDeliveryType = async (deliveryType) => {
    try {
      const response = await axios.put(`${API}/cart/delivery-type`, {
        session_id: sessionId,
        delivery_type: deliveryType,
      });
      setCart(response.data);
    } catch (error) {
      toast.error("Грешка при обновяване");
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const DELIVERY_FEE = 5.00;
  const MIN_DELIVERY_ORDER = 25.00;
  const subtotal = cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const deliveryFee = cart.delivery_type === "delivery" ? DELIVERY_FEE : 0;
  const cartTotal = subtotal + deliveryFee;
  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const canCheckoutDelivery = cart.delivery_type !== "delivery" || subtotal >= MIN_DELIVERY_ORDER;
  const amountToMinDelivery = MIN_DELIVERY_ORDER - subtotal;

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeItem,
        setDeliveryType,
        cartTotal,
        subtotal,
        deliveryFee,
        itemCount,
        loading,
        canCheckoutDelivery,
        amountToMinDelivery,
        MIN_DELIVERY_ORDER,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutData, setCheckoutData] = useState({ name: "", phone: "", address: "", notes: "", paymentMethod: "cash" });
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [orderSuccessOpen, setOrderSuccessOpen] = useState(false);
  const { cart, updateQuantity, removeItem, setDeliveryType, cartTotal, subtotal, deliveryFee, itemCount, canCheckoutDelivery, amountToMinDelivery, MIN_DELIVERY_ORDER } = useCart();
  const location = useLocation();
  const t = translations.checkout;

  const navLinks = [
    { path: "/", label: translations.nav.home },
    { path: "/store", label: translations.nav.store },
    { path: "/catering", label: translations.nav.catering },
  ];

  const handleCheckout = () => {
    setCartOpen(false);
    setCheckoutOpen(true);
    // GA4: begin_checkout
    trackBeginCheckout(cart.items, cartTotal);
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setSubmittingOrder(true);
    
    try {
      const sessionId = localStorage.getItem("granatus_session");
      
      if (checkoutData.paymentMethod === "card") {
        // Card payment - create payment intent and redirect to BORICA
        const paymentResponse = await axios.post(`${API}/payments/create-intent`, {
          amount: cartTotal,
          currency: "EUR",
          customer_name: checkoutData.name,
          customer_phone: checkoutData.phone,
        });
        
        if (paymentResponse.data.success && paymentResponse.data.payment_url && paymentResponse.data.form_fields) {
          // Create order first with pending payment status
          const orderResponse = await axios.post(`${API}/orders`, {
            session_id: sessionId,
            customer_name: checkoutData.name,
            customer_phone: checkoutData.phone,
            delivery_address: cart.delivery_type === "delivery" ? checkoutData.address : null,
            delivery_type: cart.delivery_type,
            notes: checkoutData.notes,
            items: cart.items,
            total: cartTotal,
            payment_method: "card",
            payment_status: "pending",
            order_ref: paymentResponse.data.order_ref,
          });
          
          toast.info("Пренасочване към плащане...");
          
          // Debug: log payment URL
          console.log("BORICA Payment URL:", paymentResponse.data.payment_url);
          console.log("Form fields:", Object.keys(paymentResponse.data.form_fields));
          
          // Create and submit a hidden form to BORICA
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = paymentResponse.data.payment_url;
          form.target = '_self'; // Ensure form submits in same window
          form.acceptCharset = 'UTF-8';
          
          // Add all form fields from the response
          Object.entries(paymentResponse.data.form_fields).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = String(value);
            form.appendChild(input);
          });
          
          document.body.appendChild(form);
          
          // Small delay to ensure form is in DOM
          setTimeout(() => {
            form.submit();
          }, 100);
          
          return; // Don't continue, user will be redirected
        } else {
          toast.error("Грешка при създаване на плащане. Моля, опитайте отново.");
          setSubmittingOrder(false);
          return;
        }
      }
      
      // Cash payment - create order directly
      const cashOrderResponse = await axios.post(`${API}/orders`, {
        session_id: sessionId,
        customer_name: checkoutData.name,
        customer_phone: checkoutData.phone,
        delivery_address: cart.delivery_type === "delivery" ? checkoutData.address : null,
        delivery_type: cart.delivery_type,
        notes: checkoutData.notes,
        items: cart.items,
        total: cartTotal,
        payment_method: checkoutData.paymentMethod,
        payment_status: "pending",
      });

      // GA4: purchase event (cash / on-delivery order is "purchased" from GA's POV)
      trackPurchase({
        transactionId: cashOrderResponse?.data?.id || cashOrderResponse?.data?.order_ref || sessionId,
        total: cartTotal,
        items: cart.items,
      });

      setCheckoutOpen(false);
      setCheckoutData({ name: "", phone: "", address: "", notes: "", paymentMethod: "cash" });
      setAgreedToTerms(false);
      // Clear cart after successful order (silent - no toast for each item)
      for (const item of cart.items) {
        await removeItem(item.product_id, true);
      }
      // Show success dialog
      setOrderSuccessOpen(true);
    } catch (error) {
      toast.error(t.error);
    }
    setSubmittingOrder(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-stone-100/50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24 md:h-28">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src={LOGO_URL} alt="Granatus Bakery & Cafe" className="h-16 md:h-[4.25rem] w-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path ? "text-primary" : "text-foreground/80"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Sheet open={cartOpen} onOpenChange={setCartOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-14 w-14" data-testid="cart-button">
                  <ShoppingCart className="h-8 w-8" />
                  {itemCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-6 w-6 flex items-center justify-center p-0 bg-primary text-sm">
                      {itemCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-lg" data-testid="cart-sheet">
                <SheetHeader>
                  <SheetTitle style={{ fontFamily: "'Playfair Display', serif" }}>{translations.cart.title}</SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col h-[calc(100vh-180px)]">
                  <div className="bg-muted rounded-xl p-4 mb-6">
                    <p className="text-sm font-medium mb-3">{translations.cart.orderType}</p>
                    <div className="flex gap-2">
                      <Button
                        variant={cart.delivery_type === "pickup" ? "default" : "outline"}
                        className="flex-1 rounded-full"
                        onClick={() => setDeliveryType("pickup")}
                        data-testid="pickup-option"
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        {translations.store.pickup}
                      </Button>
                      <Button
                        variant={cart.delivery_type === "delivery" ? "default" : "outline"}
                        className="flex-1 rounded-full"
                        onClick={() => setDeliveryType("delivery")}
                        data-testid="delivery-option"
                      >
                        <Truck className="h-4 w-4 mr-2" />
                        {translations.store.delivery}
                      </Button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-4">
                    {cart.items.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground" data-testid="empty-cart">
                        <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-30" />
                        <p>{translations.cart.empty}</p>
                        <Button variant="link" className="mt-2 text-primary" onClick={() => setCartOpen(false)}>
                          {translations.cart.continueShopping}
                        </Button>
                      </div>
                    ) : (
                      cart.items.map((item) => (
                        <div key={item.product_id} className="flex gap-4 p-3 bg-white rounded-xl border" data-testid={`cart-item-${item.product_id}`}>
                          <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded-lg" />
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{item.name}</h4>
                            <p className="text-primary font-semibold mt-1">{item.price.toFixed(2)} €</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity - 1)}>
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-8 text-center text-sm">{item.quantity}</span>
                              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQuantity(item.product_id, item.quantity + 1)}>
                                <Plus className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 ml-auto text-destructive hover:text-destructive" onClick={() => removeItem(item.product_id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {cart.items.length > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Междинна сума</span>
                          <span>{subtotal.toFixed(2)} €</span>
                        </div>
                        {cart.delivery_type === "delivery" && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-muted-foreground">Доставка</span>
                            <span>{deliveryFee.toFixed(2)} €</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center pt-2 border-t">
                          <span className="font-medium">{translations.cart.subtotal}</span>
                          <span className="text-xl font-bold" data-testid="cart-total">{cartTotal.toFixed(2)} €</span>
                        </div>
                      </div>
                      
                      {/* Minimum delivery order warning */}
                      {cart.delivery_type === "delivery" && !canCheckoutDelivery && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                          <p className="text-sm text-yellow-800 font-medium">
                            ⚠️ Минимална поръчка за доставка: {MIN_DELIVERY_ORDER.toFixed(2)} €
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Добавете още {amountToMinDelivery.toFixed(2)} € към кошницата
                          </p>
                        </div>
                      )}
                      
                      <Button 
                        className="w-full rounded-full h-12" 
                        onClick={handleCheckout} 
                        disabled={!canCheckoutDelivery}
                        data-testid="checkout-button"
                      >
                        {translations.cart.checkout}
                      </Button>
                      {cart.delivery_type === "delivery" && canCheckoutDelivery && (
                        <p className="text-xs text-center text-green-600 mt-3">
                          ✓ Минималната сума за доставка е достигната
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>

            {/* Checkout Dialog */}
            <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handlePlaceOrder} className="space-y-4">
                  <div className="bg-muted rounded-xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{cart.delivery_type === "pickup" ? translations.store.pickup : translations.store.delivery}</span>
                      <Badge variant="secondary">{cart.items.length} продукта</Badge>
                    </div>
                    {cart.delivery_type === "pickup" && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {t.pickupNote}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <h3 className="font-medium">{t.customerInfo}</h3>
                    <div>
                      <Label>{t.name}</Label>
                      <Input 
                        value={checkoutData.name} 
                        onChange={(e) => {
                          // Only allow letters (Cyrillic and Latin) and spaces
                          const value = e.target.value;
                          const validName = /^[а-яА-Яa-zA-ZёЁіІїЇєЄ\s\-]*$/.test(value);
                          if (validName || value === '') {
                            setCheckoutData({ ...checkoutData, name: value });
                          }
                        }}
                        required 
                        className="mt-1"
                        placeholder="Иван Петров"
                        pattern="[а-яА-Яa-zA-ZёЁіІїЇєЄ\s\-]+"
                        title="Моля въведете име и фамилия само с букви"
                        data-testid="checkout-name"
                      />
                      <p className="text-xs text-gray-500 mt-1">Само букви и интервали</p>
                    </div>
                    <div>
                      <Label>{t.phone}</Label>
                      <Input 
                        value={checkoutData.phone} 
                        onChange={(e) => setCheckoutData({ ...checkoutData, phone: e.target.value })} 
                        required 
                        className="mt-1"
                        placeholder="0878787616"
                        data-testid="checkout-phone"
                      />
                    </div>
                    {cart.delivery_type === "delivery" && (
                      <div>
                        <Label>{t.deliveryAddress}</Label>
                        <Input 
                          value={checkoutData.address} 
                          onChange={(e) => setCheckoutData({ ...checkoutData, address: e.target.value })} 
                          required 
                          className="mt-1"
                          placeholder="ул. Примерна 1, София"
                          data-testid="checkout-address"
                        />
                      </div>
                    )}
                    <div>
                      <Label>{t.notes}</Label>
                      <Textarea 
                        value={checkoutData.notes} 
                        onChange={(e) => setCheckoutData({ ...checkoutData, notes: e.target.value })} 
                        className="mt-1"
                        rows={2}
                        placeholder={t.notesPlaceholder}
                        data-testid="checkout-notes"
                      />
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="space-y-3">
                    <h3 className="font-medium">{t.paymentMethod}</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          checkoutData.paymentMethod === "cash" 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => setCheckoutData({ ...checkoutData, paymentMethod: "cash" })}
                        data-testid="payment-cash"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${checkoutData.paymentMethod === "cash" ? "bg-primary text-white" : "bg-muted"}`}>
                            <Banknote className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{t.cash}</p>
                            <p className="text-xs text-muted-foreground">{t.cashNote}</p>
                          </div>
                        </div>
                      </div>
                      <div 
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          checkoutData.paymentMethod === "card" 
                            ? "border-primary bg-primary/5" 
                            : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => setCheckoutData({ ...checkoutData, paymentMethod: "card" })}
                        data-testid="payment-card"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${checkoutData.paymentMethod === "card" ? "bg-primary text-white" : "bg-muted"}`}>
                            <CreditCard className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{t.card}</p>
                            <p className="text-xs text-muted-foreground">{t.cardNote}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    {cart.items.map((item) => (
                      <div key={item.product_id} className="flex justify-between text-sm">
                        <span>{item.name} x{item.quantity}</span>
                        <span>{(item.price * item.quantity).toFixed(2)} €</span>
                      </div>
                    ))}
                    {cart.delivery_type === "delivery" && (
                      <div className="flex justify-between text-sm pt-2 border-t">
                        <span>Доставка</span>
                        <span>{deliveryFee.toFixed(2)} €</span>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center text-lg font-bold border-t pt-4">
                    <span>{t.total}</span>
                    <span className="text-primary">{cartTotal.toFixed(2)} €</span>
                  </div>

                  {/* Terms Agreement Checkbox */}
                  <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                    <Checkbox 
                      id="terms-agreement"
                      checked={agreedToTerms}
                      onCheckedChange={setAgreedToTerms}
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-agreement" className="text-sm leading-relaxed cursor-pointer">
                      {t.agreeTerms}{" "}
                      <Link to="/terms" target="_blank" className="text-primary hover:underline font-medium" data-testid="terms-link-checkout">
                        {t.termsLink}
                      </Link>
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full rounded-full h-12" 
                    disabled={submittingOrder || !agreedToTerms} 
                    data-testid="place-order-btn"
                  >
                    {checkoutData.paymentMethod === "card" ? <CreditCard className="h-4 w-4 mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    {submittingOrder ? t.placing : (checkoutData.paymentMethod === "card" ? "Плати с карта" : t.placeOrder)}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Order Success Dialog */}
            <Dialog open={orderSuccessOpen} onOpenChange={setOrderSuccessOpen}>
              <DialogContent className="max-w-md text-center">
                <div className="py-6">
                  <div className="w-24 h-24 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <Check className="h-12 w-12 text-green-600" />
                  </div>
                  <h2 className="text-3xl font-bold text-green-600 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Поръчката е приета!
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    Благодарим ви за поръчката! Ще се свържем с вас скоро за потвърждение.
                  </p>
                  <div className="bg-muted rounded-xl p-4 mb-6">
                    <p className="text-sm text-muted-foreground">
                      📞 При въпроси, моля свържете се с нас
                    </p>
                  </div>
                  <Button 
                    className="w-full rounded-full h-12 text-lg" 
                    onClick={() => setOrderSuccessOpen(false)}
                  >
                    Продължи пазаруването
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t" data-testid="mobile-menu">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} className="block py-3 text-lg font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

// Product Card Component
const ProductCard = ({ product, featured = false, onOpenDetail }) => {
  const { addToCart, loading } = useCart();
  const navigate = useNavigate();
  const t = translations.store;
  const productUrl = `/product/${product.slug || product.id}`;

  return (
    <div
      className="group bg-white border border-border rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/50 hover:shadow-lg flex flex-col h-full cursor-pointer"
      data-testid={`product-card-${product.id}`}
      onClick={() => navigate(productUrl)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(productUrl); }}
    >
      <div className="relative overflow-hidden aspect-square">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {product.featured && <Badge className="absolute top-3 left-3 bg-accent text-white">{t.featured}</Badge>}
      </div>
      <div className="p-4 md:p-5 flex flex-col flex-grow">
        <h3 className="font-semibold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{product.name}</h3>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2 flex-grow">{product.description}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xl font-bold text-primary">{product.price.toFixed(2)} €</span>
          <Button 
            size="sm" 
            className="rounded-full" 
            onClick={(e) => { e.stopPropagation(); addToCart(product.id); }} 
            disabled={loading} 
            data-testid={`add-to-cart-${product.id}`}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t.add}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Product Detail Dialog Component
const ProductDetailDialog = ({ product, open, onClose }) => {
  const { addToCart, loading } = useCart();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const t = translations.store;

  if (!product) return null;

  const allImages = [product.image, ...(product.images || [])].filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-xl overflow-hidden bg-muted">
              <img 
                src={allImages[currentImageIndex]} 
                alt={product.name} 
                className="w-full h-full object-cover"
              />
              {product.featured && (
                <Badge className="absolute top-3 left-3 bg-accent text-white">{t.featured}</Badge>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                      currentImageIndex === idx ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                {product.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4 flex-grow">
              <p className="text-foreground/80 leading-relaxed">{product.description}</p>
              
              <div className="mt-6 p-4 bg-muted rounded-xl">
                <p className="text-sm text-muted-foreground">Категория</p>
                <p className="font-medium">{product.category}</p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-3xl font-bold text-primary">{product.price.toFixed(2)} €</span>
                {!product.available && (
                  <Badge variant="secondary">Изчерпан</Badge>
                )}
              </div>
              
              <Button 
                className="w-full rounded-full h-12" 
                onClick={() => { addToCart(product.id); onClose(); }} 
                disabled={loading || !product.available}
                data-testid="product-detail-add-to-cart"
              >
                <Plus className="h-5 w-5 mr-2" />
                Добави в количката
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Dedicated Product Page (SEO-optimized)
const ProductPage = () => {
  const { slug } = useParams();
  const { addToCart, loading } = useCart();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const t = translations.store;

  useEffect(() => {
    let active = true;
    setProduct(null);
    setRelated([]);
    setNotFound(false);
    setCurrentImageIndex(0);
    (async () => {
      try {
        // Try slug first; if the URL param looks like a UUID, fall back to /products/:id
        let res;
        try {
          res = await axios.get(`${API}/products/slug/${slug}`);
        } catch (err) {
          if (err.response?.status === 404) {
            const fallback = await axios.get(`${API}/products/${slug}`);
            res = { data: { product: fallback.data, related: [] } };
            // Redirect to canonical slug URL if one exists
            if (active && fallback.data?.slug && fallback.data.slug !== slug) {
              navigate(`/product/${fallback.data.slug}`, { replace: true });
              return;
            }
          } else {
            throw err;
          }
        }
        if (!active) return;
        setProduct(res.data.product);
        setRelated(res.data.related || []);
      } catch (err) {
        if (active) {
          console.error("Product fetch error:", err);
          setNotFound(true);
        }
      }
    })();
    return () => { active = false; };
  }, [slug, navigate]);

  // Inject product-specific JSON-LD Schema.org for rich Google snippets
  useEffect(() => {
    if (!product) return;
    // GA4: view_item event
    trackViewItem(product);
    const scriptId = "product-jsonld";
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.id = scriptId;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    const canonicalUrl = `${SITE_URL}/product/${product.slug || product.id}`;
    el.text = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      description: product.seo_description || product.description,
      image: product.image,
      category: product.category,
      brand: { "@type": "Brand", name: "Granatus Bakery & Cafe" },
      offers: {
        "@type": "Offer",
        url: canonicalUrl,
        priceCurrency: "EUR",
        price: Number(product.price).toFixed(2),
        availability: product.available
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      },
    });
    return () => { if (el && el.parentNode) el.parentNode.removeChild(el); };
  }, [product]);

  if (notFound) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen" data-testid="product-not-found">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Продуктът не е намерен
          </h1>
          <p className="text-muted-foreground mb-8">
            Този продукт може да е премахнат или адресът да е грешен.
          </p>
          <Button onClick={() => navigate("/store")} className="rounded-full" data-testid="back-to-store-btn">
            Към магазина
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen">
        <Navigation />
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="aspect-square bg-muted animate-pulse rounded-xl" />
            <div className="space-y-4">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-20 bg-muted animate-pulse rounded" />
              <div className="h-10 bg-muted animate-pulse rounded w-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const allImages = [product.image, ...(product.images || [])].filter(Boolean);
  const canonical = `${SITE_URL}/product/${product.slug || product.id}`;

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="product-page">
      <SEOHead
        title={product.seo_title || product.name}
        description={product.seo_description || product.description}
        image={product.image}
        keywords={`${product.name}, ${product.category}, Granatus Bakery`}
      />
      <Navigation />
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-8">
        {/* Breadcrumbs */}
        <nav aria-label="breadcrumb" className="flex items-center gap-2 text-sm text-muted-foreground mb-6" data-testid="product-breadcrumbs">
          <Link to="/" className="hover:text-primary transition-colors">Начало</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/store" className="hover:text-primary transition-colors">Магазин</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium truncate">{product.name}</span>
        </nav>
        <link rel="canonical" href={canonical} />

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image gallery */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-muted">
              <img
                src={allImages[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.featured && (
                <Badge className="absolute top-4 left-4 bg-accent text-white">{t.featured}</Badge>
              )}
              {!product.available && (
                <Badge variant="secondary" className="absolute top-4 right-4">Изчерпан</Badge>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="product-thumbnails">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      currentImageIndex === idx ? "border-primary" : "border-transparent"
                    }`}
                    aria-label={`Снимка ${idx + 1}`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="flex flex-col">
            <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">{product.category}</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="product-title">
              {product.name}
            </h1>
            <p className="mt-4 text-foreground/80 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {product.ingredients && (
              <div className="mt-6" data-testid="product-ingredients">
                <h2 className="font-semibold mb-1">Съставки</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{product.ingredients}</p>
              </div>
            )}

            {product.allergens && (
              <div className="mt-4" data-testid="product-allergens">
                <h2 className="font-semibold mb-1">Алергени</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{product.allergens}</p>
              </div>
            )}

            <div className="mt-8 pt-6 border-t space-y-4">
              <div className="flex items-baseline justify-between">
                <span className="text-4xl font-bold text-primary" data-testid="product-price">
                  {product.price.toFixed(2)} €
                </span>
                {product.available && (
                  <span className="text-sm text-green-600">✓ В наличност</span>
                )}
              </div>
              <Button
                size="lg"
                className="w-full rounded-full h-14 text-base"
                onClick={() => addToCart(product.id)}
                disabled={loading || !product.available}
                data-testid="product-add-to-cart"
              >
                <Plus className="h-5 w-5 mr-2" />
                Добави в количката
              </Button>
            </div>
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <section className="mt-16" data-testid="related-products">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Подобни продукти
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [siteSettings, setSiteSettings] = useState({});
  const navigate = useNavigate();
  const t = translations;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/settings`)
        ]);
        setFeaturedProducts(productsRes.data.slice(0, 4));
        setSiteSettings(settingsRes.data || {});
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="pt-20 md:pt-24" data-testid="home-page">
      <SEOHead 
        title={siteSettings.seo_title}
        description={siteSettings.seo_description}
        image={siteSettings.seo_og_image}
        keywords={siteSettings.seo_keywords}
      />
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1758945185187-16c1760ba825?w=1600" alt="Bakery Interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24">
          <div className="max-w-xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight opacity-0 animate-fade-in-up" style={{ fontFamily: "'Playfair Display', serif", animationFillMode: "forwards" }}>
              {t.hero.title1} <br />
              <span className="text-primary">{t.hero.title2}</span>
            </h1>
            <p className="mt-6 text-lg text-foreground/80 opacity-0 animate-fade-in-up animation-delay-200" style={{ animationFillMode: "forwards" }}>
              {t.hero.subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-4 opacity-0 animate-fade-in-up animation-delay-300" style={{ animationFillMode: "forwards" }}>
              <Button size="lg" className="rounded-full h-12 px-8" onClick={() => navigate("/store")} data-testid="shop-now-btn">
                {t.hero.shopNow}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-8" onClick={() => navigate("/catering")} data-testid="catering-btn">
                {t.hero.catering}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.featured.title}</h2>
            <p className="text-muted-foreground mt-2">{t.featured.subtitle}</p>
          </div>
          <Button variant="link" className="text-primary hidden md:flex" onClick={() => navigate("/store")} data-testid="view-all-btn">
            {t.featured.viewAll}
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredProducts.map((product, idx) => (
            <ProductCard key={product.id} product={product} featured={idx === 0} onOpenDetail={setSelectedProduct} />
          ))}
        </div>
        
        {/* Product Detail Dialog */}
        <ProductDetailDialog 
          product={selectedProduct} 
          open={!!selectedProduct} 
          onClose={() => setSelectedProduct(null)} 
        />
      </section>

      <section className="py-24 bg-muted" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-24 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t.philosophy.title} <span className="text-primary">{t.philosophy.titleAccent}</span>
              </h2>
              <p className="text-foreground/80 mt-6 leading-relaxed">{t.philosophy.text1}</p>
              <p className="text-foreground/80 mt-4 leading-relaxed">{t.philosophy.text2}</p>
              <div className="mt-8 flex gap-8">
                <div>
                  <div className="text-3xl font-bold text-primary">15+</div>
                  <div className="text-sm text-muted-foreground">{t.philosophy.years}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">50+</div>
                  <div className="text-sm text-muted-foreground">{t.philosophy.recipes}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-primary">100%</div>
                  <div className="text-sm text-muted-foreground">{t.philosophy.fresh}</div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <img src="https://images.unsplash.com/photo-1716237388463-14fdbfc0ca5e?w=800" alt="Baker kneading dough" className="rounded-2xl shadow-lg w-full h-[400px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto" data-testid="catering-teaser">
        <div className="bg-primary rounded-3xl overflow-hidden">
          <div className="grid md:grid-cols-2 items-center">
            <div className="p-8 md:p-12 lg:p-16 text-white">
              <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.cateringTeaser.title}</h2>
              <p className="mt-4 text-white/80">{t.cateringTeaser.subtitle}</p>
              <Button size="lg" variant="secondary" className="mt-8 rounded-full bg-white text-primary hover:bg-white/90" onClick={() => navigate("/catering")} data-testid="explore-catering-btn">
                {t.cateringTeaser.explore}
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="h-64 md:h-full">
              <img src="https://images.unsplash.com/photo-1653710209991-7b8d6f492e07?w=800" alt="Catering spread" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Store Page
const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { cart, setDeliveryType } = useCart();
  const t = translations.store;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === "All" ? `${API}/products` : `${API}/products?category=${selectedCategory}`;
        const response = await axios.get(url);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error("Грешка при зареждане на продукти");
      }
      setLoading(false);
    };
    fetchProducts();
  }, [selectedCategory]);

  // Build category list with "All" first
  const categoryList = [{ name: "All", name_bg: t.categories.All }, ...categories];

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="store-page">
      <SEOHead 
        title="Онлайн Магазин"
        description="Поръчайте пресни печива, торти и сладкиши онлайн от Granatus Bakery. Доставка в София или взимане от магазина."
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>

        <div className="bg-muted rounded-xl p-4 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="delivery-toggle">
          <div>
            <p className="font-medium">{t.orderType}</p>
            <p className="text-sm text-muted-foreground">{t.chooseOption}</p>
          </div>
          <div className="flex gap-2">
            <Button variant={cart.delivery_type === "pickup" ? "default" : "outline"} className="rounded-full" onClick={() => setDeliveryType("pickup")} data-testid="store-pickup-btn">
              <MapPin className="h-4 w-4 mr-2" />
              {t.pickup}
            </Button>
            <Button variant={cart.delivery_type === "delivery" ? "default" : "outline"} className="rounded-full" onClick={() => setDeliveryType("delivery")} data-testid="store-delivery-btn">
              <Truck className="h-4 w-4 mr-2" />
              {t.delivery}
            </Button>
          </div>
        </div>

        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8">
          <TabsList className="bg-muted p-1 rounded-full h-auto flex-wrap" data-testid="category-tabs">
            {categoryList.map((cat) => (
              <TabsTrigger key={cat.name} value={cat.name} className="rounded-full px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`category-${cat.name.toLowerCase()}`}>
                {cat.name_bg}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-muted animate-pulse rounded-xl h-80" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} onOpenDetail={setSelectedProduct} />
            ))}
          </div>
        )}

        {products.length === 0 && !loading && (
          <div className="text-center py-12 text-muted-foreground">
            <p>Няма продукти в тази категория.</p>
          </div>
        )}
      </div>
      
      {/* Product Detail Dialog */}
      <ProductDetailDialog 
        product={selectedProduct} 
        open={!!selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
      />
      
      <Footer />
    </div>
  );
};

// Catering Page
const CateringPage = () => {
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", event_date: "", guest_count: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const t = translations.catering;

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/catering/packages`);
        setPackages(response.data);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };
    fetchPackages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/catering/inquiry`, {
        ...formData,
        guest_count: parseInt(formData.guest_count),
        package_id: selectedPackage?.id || null,
      });
      toast.success(t.form.success);
      setFormData({ name: "", email: "", phone: "", event_date: "", guest_count: "", message: "" });
      setSelectedPackage(null);
    } catch (error) {
      toast.error(t.form.error);
    }
    setSubmitting(false);
  };

  return (
    <div className="pt-20 md:pt-24 min-h-screen" data-testid="catering-page">
      <SEOHead 
        title="Кетъринг"
        description="Професионални кетъринг услуги от Granatus Bakery за вашето събитие. Рождени дни, сватби, корпоративни партита."
      />
      <section className="relative py-20 bg-muted" data-testid="catering-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</h1>
          <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">{t.subtitle}</p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto" data-testid="catering-packages">
        <h2 className="text-2xl md:text-3xl font-bold mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>{t.packages}</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-white border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg ${selectedPackage?.id === pkg.id ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
              onClick={() => setSelectedPackage(pkg)}
              data-testid={`package-${pkg.id}`}
            >
              <img src={pkg.image} alt={pkg.name} className="w-full h-48 object-cover" />
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-xl font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>{pkg.name}</h3>
                  <Badge variant="secondary" className="bg-secondary/30">{pkg.price_per_person.toFixed(2)} €{t.perPerson}</Badge>
                </div>
                <p className="text-muted-foreground mt-2 text-sm">{pkg.description}</p>
                <Separator className="my-4" />
                <div className="text-sm">
                  <p className="font-medium mb-2">{t.includes}</p>
                  <ul className="space-y-1">
                    {pkg.items.map((item, idx) => (
                      <li key={idx} className="text-muted-foreground flex items-center gap-2">
                        <span className="w-1 h-1 bg-primary rounded-full" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-muted-foreground mt-4">{t.minGuests}: {pkg.min_guests}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-muted" data-testid="catering-form-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t.requestQuote}</h2>
          <p className="text-muted-foreground mb-8">{selectedPackage ? `${t.selectedPackage} ${selectedPackage.name}` : t.customNeeds}</p>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm space-y-6" data-testid="catering-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name">{t.form.name}</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-1" data-testid="catering-name-input" />
              </div>
              <div>
                <Label htmlFor="email">{t.form.email}</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-1" data-testid="catering-email-input" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone">{t.form.phone}</Label>
                <Input id="phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="mt-1" data-testid="catering-phone-input" />
              </div>
              <div>
                <Label htmlFor="event_date">{t.form.date}</Label>
                <Input id="event_date" type="date" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} required className="mt-1" data-testid="catering-date-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="guest_count">{t.form.guests}</Label>
              <Input id="guest_count" type="number" min="1" value={formData.guest_count} onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })} required className="mt-1" data-testid="catering-guests-input" />
            </div>
            <div>
              <Label htmlFor="message">{t.form.message}</Label>
              <Textarea id="message" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required rows={4} className="mt-1" placeholder={t.form.messagePlaceholder} data-testid="catering-message-input" />
            </div>
            <Button type="submit" className="w-full rounded-full h-12" disabled={submitting} data-testid="catering-submit-btn">
              {submitting ? t.form.submitting : t.form.submit}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

// Order Card Component for Admin Panel
const OrderCard = ({ order, onStatusChange, onDelete }) => {
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  
  const currentStatus = ORDER_STATUS_OPTIONS.find(s => s.value === order.status) || ORDER_STATUS_OPTIONS[0];
  
  const handleStatusChange = async (newStatus) => {
    if (newStatus === order.status) {
      setShowStatusMenu(false);
      return;
    }
    
    setIsChangingStatus(true);
    await onStatusChange(order.id, newStatus);
    setIsChangingStatus(false);
    setShowStatusMenu(false);
  };
  
  return (
    <Card data-testid={`order-card-${order.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
          {/* Order Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3 className="font-semibold text-lg">{order.customer_name}</h3>
              <Badge variant="outline">{order.delivery_type === "pickup" ? "Взимане" : "Доставка"}</Badge>
              <Badge variant={order.payment_method === "card" ? "default" : "secondary"} className={order.payment_method === "card" ? "bg-blue-500" : ""}>
                {order.payment_method === "card" ? "Карта" : "В брой"}
              </Badge>
              {order.payment_status && order.payment_method === "card" && (
                <Badge variant={order.payment_status === "paid" ? "default" : "secondary"} className={order.payment_status === "paid" ? "bg-green-500" : "bg-yellow-500"}>
                  {order.payment_status === "paid" ? "Платено" : order.payment_status === "processing" ? "Обработва се" : "Чака плащане"}
                </Badge>
              )}
            </div>
            
            <div className="space-y-1 text-sm">
              <p className="text-muted-foreground flex items-center gap-2">
                <Phone className="h-3.5 w-3.5" />
                {order.customer_phone}
              </p>
              {order.delivery_address && (
                <p className="text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-3.5 w-3.5" />
                  {order.delivery_address}
                </p>
              )}
            </div>
            
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm font-medium mb-1">Продукти:</p>
              <div className="flex flex-wrap gap-2">
                {order.items.map((item, idx) => (
                  <span key={idx} className="text-sm bg-white px-2 py-1 rounded border">
                    {item.name} x{item.quantity}
                  </span>
                ))}
              </div>
            </div>
            
            {order.notes && (
              <p className="text-sm mt-2 text-foreground/70 bg-yellow-50 p-2 rounded border border-yellow-200">
                📝 {order.notes}
              </p>
            )}
          </div>
          
          {/* Status & Actions */}
          <div className="lg:text-right space-y-3">
            <div>
              <p className="text-2xl font-bold text-primary">{order.total.toFixed(2)} €</p>
              <p className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString("bg-BG")}</p>
            </div>
            
            {/* Status Dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                className={`w-full lg:w-48 justify-between ${currentStatus.color} text-white hover:opacity-90`}
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                disabled={isChangingStatus}
                data-testid={`order-status-btn-${order.id}`}
              >
                {isChangingStatus ? "..." : currentStatus.label}
                <ChevronRight className={`h-4 w-4 transition-transform ${showStatusMenu ? "rotate-90" : ""}`} />
              </Button>
              
              {showStatusMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-50 p-2" data-testid="status-dropdown">
                  <p className="text-xs text-muted-foreground px-2 py-1 mb-1">Промяна на статус:</p>
                  {ORDER_STATUS_OPTIONS.map((status) => (
                    <button
                      key={status.value}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-colors ${
                        order.status === status.value 
                          ? "bg-muted font-medium" 
                          : "hover:bg-muted/50"
                      }`}
                      onClick={() => handleStatusChange(status.value)}
                      data-testid={`status-option-${status.value}`}
                    >
                      <span className={`w-3 h-3 rounded-full ${status.color}`}></span>
                      {status.label}
                      {order.status === status.value && <Check className="h-4 w-4 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Delete Button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 mt-2"
              onClick={() => {
                if (window.confirm("Сигурни ли сте, че искате да изтриете тази поръчка?")) {
                  onDelete(order.id);
                }
              }}
              data-testid={`delete-order-${order.id}`}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Изтрий
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const t = translations.admin;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success) {
        localStorage.setItem("admin_token", response.data.token);
        toast.success("Успешен вход!");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      toast.error("Невалидни данни за вход");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="Granatus" className="h-16 mx-auto mb-4" />
          <CardTitle style={{ fontFamily: "'Playfair Display', serif" }}>{t.login}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="username">{t.username}</Label>
              <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="mt-1" data-testid="admin-username" />
            </div>
            <div>
              <Label htmlFor="password">{t.password}</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" data-testid="admin-password" />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="admin-login-btn">
              <Lock className="h-4 w-4 mr-2" />
              {loading ? "..." : t.loginBtn}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("products");
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();
  const t = translations.admin;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Basic ${token}` };
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodRes, catRes, pkgRes, inqRes, ordRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/catering/packages`),
        axios.get(`${API}/admin/catering/inquiries`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/orders`, { headers: getAuthHeaders() }),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setPackages(pkgRes.data);
      setInquiries(inqRes.data);
      setOrders(ordRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct?.id) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers: getAuthHeaders() });
        toast.success("Продуктът е обновен!");
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers: getAuthHeaders() });
        toast.success("Продуктът е добавен!");
      }
      fetchData();
      setShowProductDialog(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error("Грешка при запазване");
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await axios.delete(`${API}/admin/products/${id}`, { headers: getAuthHeaders() });
      toast.success("Продуктът е изтрит!");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Грешка при изтриване");
    }
  };

  const handleSavePackage = async (packageData) => {
    try {
      if (editingPackage?.id) {
        await axios.put(`${API}/admin/catering/packages/${editingPackage.id}`, packageData, { headers: getAuthHeaders() });
        toast.success("Пакетът е обновен!");
      } else {
        await axios.post(`${API}/admin/catering/packages`, packageData, { headers: getAuthHeaders() });
        toast.success("Пакетът е добавен!");
      }
      fetchData();
      setShowPackageDialog(false);
      setEditingPackage(null);
    } catch (error) {
      toast.error("Грешка при запазване");
    }
  };

  const handleDeletePackage = async (id) => {
    try {
      await axios.delete(`${API}/admin/catering/packages/${id}`, { headers: getAuthHeaders() });
      toast.success("Пакетът е изтрит!");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Грешка при изтриване");
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (editingCategory?.id) {
        await axios.put(`${API}/admin/categories/${editingCategory.id}`, categoryData, { headers: getAuthHeaders() });
        toast.success("Категорията е обновена!");
      } else {
        await axios.post(`${API}/admin/categories`, categoryData, { headers: getAuthHeaders() });
        toast.success("Категорията е добавена!");
      }
      fetchData();
      setShowCategoryDialog(false);
      setEditingCategory(null);
    } catch (error) {
      toast.error("Грешка при запазване");
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await axios.delete(`${API}/admin/categories/${id}`, { headers: getAuthHeaders() });
      toast.success("Категорията е изтрита!");
      fetchData();
      setDeleteConfirm(null);
    } catch (error) {
      toast.error("Грешка при изтриване");
    }
  };

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="Granatus" className="h-10" />
          <h1 className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.dashboard}</h1>
        </div>
        <Button variant="outline" onClick={handleLogout} data-testid="admin-logout">
          <LogOut className="h-4 w-4 mr-2" />
          {t.logout}
        </Button>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="products" className="gap-2"><Package className="h-4 w-4" />{t.products}</TabsTrigger>
            <TabsTrigger value="categories" className="gap-2"><Settings className="h-4 w-4" />Категории</TabsTrigger>
            <TabsTrigger value="orders" className="gap-2"><ShoppingCart className="h-4 w-4" />Поръчки</TabsTrigger>
            <TabsTrigger value="packages" className="gap-2"><Package className="h-4 w-4" />{t.cateringPkg}</TabsTrigger>
            <TabsTrigger value="inquiries" className="gap-2"><Users className="h-4 w-4" />{t.inquiries}</TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 bg-primary/10" data-testid="settings-tab"><Bell className="h-4 w-4" />Настройки</TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <div className={activeTab === "products" ? "" : "hidden"}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.products}</h2>
              <Button onClick={() => { setEditingProduct({}); setShowProductDialog(true); }} data-testid="add-product-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t.addProduct}
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <Card key={product.id} className="overflow-hidden" data-testid={`admin-product-${product.id}`}>
                  <img src={product.image} alt={product.name} className="w-full h-40 object-cover" />
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{product.name}</h3>
                        <p className="text-sm text-muted-foreground">{categories.find(c => c.name === product.category)?.name_bg || product.category}</p>
                        <p className="text-primary font-bold mt-1">{product.price.toFixed(2)} €</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setEditingProduct(product); setShowProductDialog(true); }} data-testid={`edit-product-${product.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteConfirm({ type: "product", id: product.id })} data-testid={`delete-product-${product.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Categories Tab */}
          <div className={activeTab === "categories" ? "" : "hidden"}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Категории</h2>
              <Button onClick={() => { setEditingCategory({}); setShowCategoryDialog(true); }} data-testid="add-category-btn">
                <Plus className="h-4 w-4 mr-2" />
                Добави категория
              </Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <Card key={category.id} data-testid={`admin-category-${category.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{category.name_bg}</h3>
                        <p className="text-sm text-muted-foreground">Код: {category.name}</p>
                        <p className="text-sm text-muted-foreground">Подредба: {category.order}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setEditingCategory(category); setShowCategoryDialog(true); }} data-testid={`edit-category-${category.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteConfirm({ type: "category", id: category.id })} data-testid={`delete-category-${category.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {categories.length === 0 && (
              <p className="text-muted-foreground text-center py-12">Няма категории. Добавете първата категория.</p>
            )}
          </div>

          {/* Orders Tab */}
          <div className={activeTab === "orders" ? "" : "hidden"}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Поръчки</h2>
            {orders.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">Няма поръчки</p>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <OrderCard 
                    key={order.id} 
                    order={order} 
                    onStatusChange={async (orderId, newStatus) => {
                      try {
                        await axios.put(
                          `${API}/admin/orders/${orderId}/status`, 
                          { status: newStatus },
                          { headers: getAuthHeaders() }
                        );
                        toast.success(`Статусът е обновен на "${ORDER_STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus}"`);
                        fetchData();
                      } catch (error) {
                        toast.error("Грешка при обновяване на статуса");
                      }
                    }}
                    onDelete={async (orderId) => {
                      try {
                        await axios.delete(
                          `${API}/admin/orders/${orderId}`,
                          { headers: getAuthHeaders() }
                        );
                        toast.success("Поръчката е изтрита");
                        fetchData();
                      } catch (error) {
                        toast.error("Грешка при изтриване на поръчката");
                      }
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Packages Tab */}
          <div className={activeTab === "packages" ? "" : "hidden"}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>{t.cateringPkg}</h2>
              <Button onClick={() => { setEditingPackage({}); setShowPackageDialog(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Добави пакет
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {packages.map((pkg) => (
                <Card key={pkg.id} className="overflow-hidden">
                  <img src={pkg.image} alt={pkg.name} className="w-full h-40 object-cover" />
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{pkg.name}</h3>
                        <p className="text-primary font-bold mt-1">{pkg.price_per_person.toFixed(2)} €/човек</p>
                        <p className="text-sm text-muted-foreground">Мин. {pkg.min_guests} гости</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setEditingPackage(pkg); setShowPackageDialog(true); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteConfirm({ type: "package", id: pkg.id })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Inquiries Tab */}
          <div className={activeTab === "inquiries" ? "" : "hidden"}>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t.inquiries}</h2>
            {inquiries.length === 0 ? (
              <p className="text-muted-foreground text-center py-12">{t.noInquiries}</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <Card key={inq.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold">{inq.name}</h3>
                          <p className="text-sm text-muted-foreground">{inq.email} | {inq.phone}</p>
                          <p className="text-sm mt-2">Дата: {inq.event_date} | Гости: {inq.guest_count}</p>
                          <p className="text-sm mt-2 text-foreground/80">{inq.message}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant="secondary">{new Date(inq.created_at).toLocaleDateString("bg-BG")}</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={async () => {
                              if (window.confirm("Сигурни ли сте, че искате да изтриете тази заявка?")) {
                                try {
                                  await axios.delete(`${API}/admin/inquiries/${inq.id}`, { headers: getAuthHeaders() });
                                  toast.success("Заявката е изтрита");
                                  fetchData();
                                } catch (error) {
                                  toast.error("Грешка при изтриване на заявката");
                                }
                              }
                            }}
                            data-testid={`delete-inquiry-${inq.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Settings Tab */}
          <div className={activeTab === "settings" ? "" : "hidden"}>
            <SettingsTab authHeaders={getAuthHeaders} />
          </div>
        </Tabs>
      </div>

      {/* Product Dialog */}
      <ProductDialog
        open={showProductDialog}
        onClose={() => { setShowProductDialog(false); setEditingProduct(null); }}
        product={editingProduct}
        categories={categories}
        onSave={handleSaveProduct}
      />

      {/* Package Dialog */}
      <PackageDialog
        open={showPackageDialog}
        onClose={() => { setShowPackageDialog(false); setEditingPackage(null); }}
        pkg={editingPackage}
        onSave={handleSavePackage}
      />

      {/* Category Dialog */}
      <CategoryDialog
        open={showCategoryDialog}
        onClose={() => { setShowCategoryDialog(false); setEditingCategory(null); }}
        category={editingCategory}
        onSave={handleSaveCategory}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.confirmDelete}</DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t.no}</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteConfirm?.type === "product") handleDeleteProduct(deleteConfirm.id);
                else if (deleteConfirm?.type === "package") handleDeletePackage(deleteConfirm.id);
                else if (deleteConfirm?.type === "category") handleDeleteCategory(deleteConfirm.id);
              }}
            >
              {t.yes}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ authHeaders }) => {
  const [siteSettings, setSiteSettings] = useState({
    hero_title1: "Занаятчийски печива",
    hero_title2: "Пресни всеки ден",
    hero_subtitle: "",
    philosophy_title: "Направено с",
    philosophy_title_accent: "Страст",
    philosophy_text1: "",
    philosophy_text2: "",
    footer_description: "",
    footer_address: "ул. Искър 33, София",
    footer_phone: "0878787616",
    footer_hours: "Пон, Ср-Нед: 7:00 - 19:00",
    terms_and_conditions: "",
    privacy_policy: "",
    instagram_url: "",
    facebook_url: "",
    // SEO Settings
    seo_title: "Granatus Bakery & Cafe",
    seo_description: "Занаятчийска пекарна и кафене в София. Пресни печива, торти и сладкиши всеки ден.",
    seo_keywords: "пекарна, кафене, хляб, торти, сладкиши, София, доставка",
    seo_og_image: "",
    // Analytics
    google_analytics_id: "",
  });
  const [notificationSettings, setNotificationSettings] = useState({
    telegram_enabled: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    viber_enabled: false,
    viber_bot_token: "",
    viber_user_id: "",
    viber_phone: "+359878787616",
  });
  const [saving, setSaving] = useState(false);
  const [testingNotifications, setTestingNotifications] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState("texts");
  const t = translations.settings;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [siteRes, notifRes] = await Promise.all([
          axios.get(`${API}/admin/settings`, { headers: authHeaders() }),
          axios.get(`${API}/admin/notifications/settings`, { headers: authHeaders() }),
        ]);
        if (siteRes.data) setSiteSettings(prev => ({ ...prev, ...siteRes.data }));
        if (notifRes.data) setNotificationSettings(prev => ({ ...prev, ...notifRes.data }));
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSaveSiteSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, siteSettings, { headers: authHeaders() });
      toast.success(t.saved);
    } catch (error) {
      toast.error("Грешка при запазване");
    }
    setSaving(false);
  };

  const handleSaveNotificationSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/notifications/settings`, notificationSettings, { headers: authHeaders() });
      toast.success(t.saved);
    } catch (error) {
      toast.error("Грешка при запазване");
    }
    setSaving(false);
  };

  const handleTestNotifications = async () => {
    setTestingNotifications(true);
    try {
      const response = await axios.post(`${API}/admin/notifications/test`, {}, { headers: authHeaders() });
      const results = response.data.results;
      if (results.telegram === "success") toast.success("Telegram: Успешно!");
      else if (results.telegram) toast.error(`Telegram: ${results.telegram}`);
      if (results.viber === "success") toast.success("Viber: Успешно!");
      else if (results.viber) toast.error(`Viber: ${results.viber}`);
      if (!results.telegram && !results.viber) toast.info("Няма активирани известия за тест");
    } catch (error) {
      toast.error("Грешка при тестване");
    }
    setTestingNotifications(false);
  };

  const handleExportOrders = () => {
    const token = localStorage.getItem("admin_token");
    window.open(`${API}/admin/orders/export?format=csv`, "_blank");
  };

  return (
    <div data-testid="settings-content">
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>{t.title}</h2>
      
      {/* Settings Sub-tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button 
          variant={activeSettingsTab === "texts" ? "default" : "outline"} 
          size="sm"
          onClick={() => setActiveSettingsTab("texts")}
          data-testid="settings-texts-btn"
        >
          <FileText className="h-4 w-4 mr-2" />
          {t.siteTexts}
        </Button>
        <Button 
          variant={activeSettingsTab === "notifications" ? "default" : "outline"} 
          size="sm"
          onClick={() => setActiveSettingsTab("notifications")}
          data-testid="settings-notifications-btn"
        >
          <Bell className="h-4 w-4 mr-2" />
          {t.notifications}
        </Button>
        <Button 
          variant={activeSettingsTab === "export" ? "default" : "outline"} 
          size="sm"
          onClick={() => setActiveSettingsTab("export")}
          data-testid="settings-export-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          {t.export}
        </Button>
      </div>

      {/* Site Texts Settings */}
      {activeSettingsTab === "texts" && (
        <div className="space-y-6">
          {/* Hero Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.heroSection}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>{t.heroTitle1}</Label>
                  <Input 
                    value={siteSettings.hero_title1} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, hero_title1: e.target.value })}
                    data-testid="hero-title1-input"
                  />
                </div>
                <div>
                  <Label>{t.heroTitle2}</Label>
                  <Input 
                    value={siteSettings.hero_title2} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, hero_title2: e.target.value })}
                    data-testid="hero-title2-input"
                  />
                </div>
              </div>
              <div>
                <Label>{t.heroSubtitle}</Label>
                <Textarea 
                  value={siteSettings.hero_subtitle} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, hero_subtitle: e.target.value })}
                  rows={2}
                  data-testid="hero-subtitle-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Footer Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.footerSection}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t.footerDescription}</Label>
                <Textarea 
                  value={siteSettings.footer_description} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, footer_description: e.target.value })}
                  rows={2}
                  data-testid="footer-desc-input"
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>{t.footerAddress}</Label>
                  <Input 
                    value={siteSettings.footer_address} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, footer_address: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t.footerPhone}</Label>
                  <Input 
                    value={siteSettings.footer_phone} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, footer_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>{t.footerHours}</Label>
                  <Input 
                    value={siteSettings.footer_hours} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, footer_hours: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social Media Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Instagram className="h-5 w-5" />
                {t.socialSection}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2"><Instagram className="h-4 w-4 text-pink-500" />{t.instagramUrl}</Label>
                  <Input 
                    value={siteSettings.instagram_url || ""} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, instagram_url: e.target.value })}
                    placeholder="https://instagram.com/granatus_bakery"
                    data-testid="instagram-url-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2"><Facebook className="h-4 w-4 text-blue-600" />{t.facebookUrl}</Label>
                  <Input 
                    value={siteSettings.facebook_url || ""} 
                    onChange={(e) => setSiteSettings({ ...siteSettings, facebook_url: e.target.value })}
                    placeholder="https://facebook.com/granatus.bakery"
                    data-testid="facebook-url-input"
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">Тези линкове ще се показват във footer-а на сайта.</p>
            </CardContent>
          </Card>

          {/* SEO Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t.seoSection}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>{t.seoTitle}</Label>
                <Input 
                  value={siteSettings.seo_title || ""} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, seo_title: e.target.value })}
                  placeholder="Granatus Bakery & Cafe - Занаятчийска пекарна в София"
                  data-testid="seo-title-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Показва се в заглавието на браузъра и в резултатите от търсене ({(siteSettings.seo_title || "").length}/60 символа)</p>
              </div>
              <div>
                <Label>{t.seoDescription}</Label>
                <Textarea 
                  value={siteSettings.seo_description || ""} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, seo_description: e.target.value })}
                  placeholder="Занаятчийска пекарна и кафене в София. Пресни печива, торти и сладкиши всеки ден."
                  rows={3}
                  data-testid="seo-description-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Описание за търсачки ({(siteSettings.seo_description || "").length}/160 символа)</p>
              </div>
              <div>
                <Label>{t.seoKeywords}</Label>
                <Input 
                  value={siteSettings.seo_keywords || ""} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, seo_keywords: e.target.value })}
                  placeholder="пекарна, кафене, хляб, торти, сладкиши, София"
                  data-testid="seo-keywords-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Ключови думи, разделени със запетая</p>
              </div>
              <div>
                <Label>{t.seoOgImage}</Label>
                <Input 
                  value={siteSettings.seo_og_image || ""} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, seo_og_image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  data-testid="seo-og-image-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Снимка при споделяне в социални мрежи (препоръчително 1200x630px)</p>
              </div>

              {/* Google Analytics */}
              <div className="border-t pt-6">
                <Label className="flex items-center gap-2">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#F9AB00"><path d="M22.84 2.998v17.999a2.983 2.983 0 0 1-2.967 2.998 2.98 2.98 0 0 1-2.956-2.998V2.998A2.97 2.97 0 0 1 19.84 0a2.983 2.983 0 0 1 3 2.998zM12 9.003a2.983 2.983 0 0 0-3 2.998v8.998a2.983 2.983 0 0 0 2.956 2.998 2.98 2.98 0 0 0 2.956-2.998v-8.998A2.98 2.98 0 0 0 12 9.003zM4.159 15a2.983 2.983 0 0 0-2.999 2.998A2.98 2.98 0 0 0 4.116 21a2.983 2.983 0 0 0 2.999-2.998A2.98 2.98 0 0 0 4.159 15z"/></svg>
                  Google Analytics Measurement ID
                </Label>
                <Input
                  value={siteSettings.google_analytics_id || ""}
                  onChange={(e) => setSiteSettings({ ...siteSettings, google_analytics_id: e.target.value.trim() })}
                  placeholder="G-XXXXXXXXXX"
                  data-testid="google-analytics-id-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  GA4 Measurement ID (започва с <code>G-</code>). Вземете го от{" "}
                  <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">
                    analytics.google.com
                  </a>
                  {" "}→ Admin → Data Streams → твоят web stream.
                  Проследяват се: page views, view_item, add_to_cart, begin_checkout, purchase.
                  Оставете празно, за да изключите проследяването.
                </p>
              </div>
              
              {/* Social Media Preview */}
              <div className="border-t pt-6">
                <Label className="text-base font-semibold mb-4 block">📱 Преглед при споделяне в социални мрежи</Label>
                
                {/* Facebook Preview */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Facebook / LinkedIn</p>
                  <div className="border rounded-lg overflow-hidden max-w-md bg-white shadow-sm">
                    {siteSettings.seo_og_image ? (
                      <img src={siteSettings.seo_og_image} alt="OG Preview" className="w-full h-52 object-cover" />
                    ) : (
                      <div className="w-full h-52 bg-muted flex items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="p-3 border-t bg-gray-50">
                      <p className="text-xs text-muted-foreground uppercase">granatus.eu</p>
                      <p className="font-semibold text-sm line-clamp-1">{siteSettings.seo_title || "Granatus Bakery & Cafe"}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{siteSettings.seo_description || "Занаятчийска пекарна и кафене в София."}</p>
                    </div>
                  </div>
                </div>
                
                {/* Twitter Preview */}
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Twitter / X</p>
                  <div className="border rounded-2xl overflow-hidden max-w-md bg-white shadow-sm">
                    {siteSettings.seo_og_image ? (
                      <img src={siteSettings.seo_og_image} alt="Twitter Preview" className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-muted flex items-center justify-center">
                        <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                      </div>
                    )}
                    <div className="p-3 border-t">
                      <p className="font-semibold text-sm line-clamp-1">{siteSettings.seo_title || "Granatus Bakery & Cafe"}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{siteSettings.seo_description || "Занаятчийска пекарна и кафене в София."}</p>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <span>🔗</span> granatus.eu
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Terms Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t.termsSection}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{t.termsContent}</Label>
                <Textarea 
                  value={siteSettings.terms_and_conditions} 
                  onChange={(e) => setSiteSettings({ ...siteSettings, terms_and_conditions: e.target.value })}
                  rows={8}
                  placeholder="Въведете общите условия тук..."
                  data-testid="terms-input"
                />
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSaveSiteSettings} disabled={saving} className="w-full md:w-auto" data-testid="save-settings-btn">
            {saving ? t.saving : t.save}
          </Button>
        </div>
      )}

      {/* Notification Settings */}
      {activeSettingsTab === "notifications" && (
        <div className="space-y-6">
          {/* Telegram */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-500" />
                {t.telegramSettings}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Switch 
                  checked={notificationSettings.telegram_enabled} 
                  onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, telegram_enabled: v })}
                  data-testid="telegram-enabled-switch"
                />
                <Label>{t.enabled}</Label>
              </div>
              {notificationSettings.telegram_enabled && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t.botToken}</Label>
                      <Input 
                        value={notificationSettings.telegram_bot_token} 
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, telegram_bot_token: e.target.value })}
                        placeholder="123456:ABC-DEF..."
                        type="password"
                        data-testid="telegram-token-input"
                      />
                    </div>
                    <div>
                      <Label>{t.chatId}</Label>
                      <Input 
                        value={notificationSettings.telegram_chat_id} 
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, telegram_chat_id: e.target.value })}
                        placeholder="-123456789"
                        data-testid="telegram-chatid-input"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{t.telegramNote}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Viber */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-purple-500" />
                {t.viberSettings}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Switch 
                  checked={notificationSettings.viber_enabled} 
                  onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, viber_enabled: v })}
                  data-testid="viber-enabled-switch"
                />
                <Label>{t.enabled}</Label>
              </div>
              {notificationSettings.viber_enabled && (
                <>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t.botToken}</Label>
                      <Input 
                        value={notificationSettings.viber_bot_token} 
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, viber_bot_token: e.target.value })}
                        placeholder="Viber Bot Auth Token"
                        type="password"
                        data-testid="viber-token-input"
                      />
                    </div>
                    <div>
                      <Label>{t.userId}</Label>
                      <Input 
                        value={notificationSettings.viber_user_id} 
                        onChange={(e) => setNotificationSettings({ ...notificationSettings, viber_user_id: e.target.value })}
                        placeholder="User ID от Viber"
                        data-testid="viber-userid-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>{t.phone}</Label>
                    <Input 
                      value={notificationSettings.viber_phone} 
                      onChange={(e) => setNotificationSettings({ ...notificationSettings, viber_phone: e.target.value })}
                      placeholder="+359878787616"
                      data-testid="viber-phone-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Телефонният номер е за справка</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-sm text-yellow-800">{t.viberNote}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-4 flex-wrap">
            <Button onClick={handleSaveNotificationSettings} disabled={saving} data-testid="save-notifications-btn">
              {saving ? t.saving : t.save}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleTestNotifications} 
              disabled={testingNotifications}
              data-testid="test-notifications-btn"
            >
              {testingNotifications ? t.testing : t.testNotification}
            </Button>
          </div>
        </div>
      )}

      {/* Export Settings */}
      {activeSettingsTab === "export" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="h-5 w-5" />
                {t.exportOrders}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">{t.exportDesc}</p>
              <Button onClick={handleExportOrders} data-testid="export-orders-btn">
                <Download className="h-4 w-4 mr-2" />
                Изтегли CSV
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Product Dialog Component
const ProductDialog = ({ open, onClose, product, categories, onSave }) => {
  const [formData, setFormData] = useState({ name: "", description: "", price: "", category: "Bread", image: "", images: [], featured: false, available: true, slug: "", ingredients: "", allergens: "", seo_title: "", seo_description: "" });
  const [newImageUrl, setNewImageUrl] = useState("");
  const t = translations.admin;

  useEffect(() => {
    if (product?.id) {
      setFormData({
        name: product.name || "",
        description: product.description || "",
        price: product.price?.toString() || "",
        category: product.category || "Bread",
        image: product.image || "",
        images: product.images || [],
        featured: product.featured || false,
        available: product.available !== false,
        slug: product.slug || "",
        ingredients: product.ingredients || "",
        allergens: product.allergens || "",
        seo_title: product.seo_title || "",
        seo_description: product.seo_description || "",
      });
    } else {
      setFormData({ name: "", description: "", price: "", category: "Bread", image: "", images: [], featured: false, available: true, slug: "", ingredients: "", allergens: "", seo_title: "", seo_description: "" });
    }
    setNewImageUrl("");
  }, [product]);

  const handleAddImage = () => {
    if (newImageUrl && !formData.images.includes(newImageUrl)) {
      setFormData({ ...formData, images: [...formData.images, newImageUrl] });
      setNewImageUrl("");
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.price),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product?.id ? t.editProduct : t.addProduct}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>{t.productName}</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
          <div>
            <Label>{t.productDesc}</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t.productPrice}</Label>
              <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
            </div>
            <div>
              <Label>{t.productCategory}</Label>
              <select className="w-full h-10 rounded-md border px-3" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                {categories.map((cat) => (
                  <option key={cat.id || cat.name} value={cat.name}>{cat.name_bg || cat.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label>{t.productImage} (основна)</Label>
            <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="https://..." required />
            {formData.image && <img src={formData.image} alt="Preview" className="mt-2 h-32 w-full object-cover rounded-lg" />}
          </div>
          
          {/* Additional Images */}
          <div>
            <Label>Допълнителни снимки</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                value={newImageUrl} 
                onChange={(e) => setNewImageUrl(e.target.value)} 
                placeholder="https://..." 
                className="flex-grow"
              />
              <Button type="button" variant="outline" onClick={handleAddImage} disabled={!newImageUrl}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.images.length > 0 && (
              <div className="mt-3 grid grid-cols-4 gap-2">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <img src={img} alt={`Extra ${idx + 1}`} className="h-16 w-full object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={formData.featured} onCheckedChange={(v) => setFormData({ ...formData, featured: v })} />
              <Label>{t.productFeatured}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.available} onCheckedChange={(v) => setFormData({ ...formData, available: v })} />
              <Label>{t.productAvailable}</Label>
            </div>
          </div>

          {/* Ingredients & Allergens (shown on product page) */}
          <div>
            <Label>Съставки (по желание)</Label>
            <Textarea
              value={formData.ingredients}
              onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
              rows={2}
              placeholder="Брашно, вода, мая, сол..."
              data-testid="product-ingredients-input"
            />
          </div>
          <div>
            <Label>Алергени (по желание)</Label>
            <Input
              value={formData.allergens}
              onChange={(e) => setFormData({ ...formData, allergens: e.target.value })}
              placeholder="Глутен, яйца, млечни продукти..."
              data-testid="product-allergens-input"
            />
          </div>

          {/* SEO fields */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-2 text-muted-foreground">SEO настройки (по желание)</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">URL slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="auto-generated-from-name"
                  data-testid="product-slug-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Оставете празно за авто-генериране. URL: /product/{formData.slug || '...'}
                </p>
              </div>
              <div>
                <Label className="text-xs">SEO заглавие</Label>
                <Input
                  value={formData.seo_title}
                  onChange={(e) => setFormData({ ...formData, seo_title: e.target.value })}
                  placeholder="Остави празно за използване на името"
                  data-testid="product-seo-title-input"
                />
              </div>
              <div>
                <Label className="text-xs">SEO описание</Label>
                <Textarea
                  value={formData.seo_description}
                  onChange={(e) => setFormData({ ...formData, seo_description: e.target.value })}
                  rows={2}
                  placeholder="До 160 знака за Google резултати"
                  data-testid="product-seo-description-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t.cancel}</Button>
            <Button type="submit">{t.save}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Package Dialog Component
const PackageDialog = ({ open, onClose, pkg, onSave }) => {
  const [formData, setFormData] = useState({ name: "", description: "", price_per_person: "", min_guests: "", items: "", image: "" });

  useEffect(() => {
    if (pkg?.id) {
      setFormData({
        name: pkg.name || "",
        description: pkg.description || "",
        price_per_person: pkg.price_per_person?.toString() || "",
        min_guests: pkg.min_guests?.toString() || "",
        items: pkg.items?.join("\n") || "",
        image: pkg.image || "",
      });
    } else {
      setFormData({ name: "", description: "", price_per_person: "", min_guests: "", items: "", image: "" });
    }
  }, [pkg]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      description: formData.description,
      price_per_person: parseFloat(formData.price_per_person),
      min_guests: parseInt(formData.min_guests),
      items: formData.items.split("\n").filter((i) => i.trim()),
      image: formData.image,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{pkg?.id ? "Редактирай пакет" : "Добави пакет"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Име на пакета</Label>
            <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          </div>
          <div>
            <Label>Описание</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Цена на човек (лв)</Label>
              <Input type="number" step="0.01" value={formData.price_per_person} onChange={(e) => setFormData({ ...formData, price_per_person: e.target.value })} required />
            </div>
            <div>
              <Label>Минимум гости</Label>
              <Input type="number" value={formData.min_guests} onChange={(e) => setFormData({ ...formData, min_guests: e.target.value })} required />
            </div>
          </div>
          <div>
            <Label>Включени артикули (по един на ред)</Label>
            <Textarea value={formData.items} onChange={(e) => setFormData({ ...formData, items: e.target.value })} required rows={5} placeholder="Кроасани\nКафе\nПлодове" />
          </div>
          <div>
            <Label>Снимка URL</Label>
            <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="https://..." required />
            {formData.image && <img src={formData.image} alt="Preview" className="mt-2 h-32 w-full object-cover rounded-lg" />}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Отказ</Button>
            <Button type="submit">Запази</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Category Dialog Component
const CategoryDialog = ({ open, onClose, category, onSave }) => {
  const [formData, setFormData] = useState({ name: "", name_bg: "", order: "0" });

  useEffect(() => {
    if (category?.id) {
      setFormData({
        name: category.name || "",
        name_bg: category.name_bg || "",
        order: category.order?.toString() || "0",
      });
    } else {
      setFormData({ name: "", name_bg: "", order: "0" });
    }
  }, [category]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      name_bg: formData.name_bg,
      order: parseInt(formData.order),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{category?.id ? "Редактирай категория" : "Добави категория"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Име на български *</Label>
            <Input 
              value={formData.name_bg} 
              onChange={(e) => setFormData({ ...formData, name_bg: e.target.value })} 
              required 
              placeholder="напр. Сладкиши"
            />
            <p className="text-xs text-muted-foreground mt-1">Това име ще се показва на сайта</p>
          </div>
          <div>
            <Label>Код (на латиница) *</Label>
            <Input 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              required 
              placeholder="напр. Pastries"
            />
            <p className="text-xs text-muted-foreground mt-1">Използва се за системата (без интервали)</p>
          </div>
          <div>
            <Label>Подредба</Label>
            <Input 
              type="number" 
              value={formData.order} 
              onChange={(e) => setFormData({ ...formData, order: e.target.value })} 
              placeholder="0"
            />
            <p className="text-xs text-muted-foreground mt-1">По-малко число = показва се първо</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Отказ</Button>
            <Button type="submit">Запази</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Payment Return Page - Customer is redirected here after payment
const PaymentReturnPage = () => {
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const orderRef = params.get("ref") || params.get("order_id");
      const statusParam = params.get("status");
      
      if (!orderRef) {
        setStatus("error");
        return;
      }

      // If status is already in URL (from backend redirect), use it
      if (statusParam === "success") {
        try {
          const response = await axios.get(`${API}/payments/check`, {
            params: { ref: orderRef }
          });
          if (response.data.success) {
            setPaymentData(response.data);
            setStatus("success");
            _firePurchaseOnce(orderRef, response.data);
          } else {
            setStatus("success"); // Still show success since backend confirmed it
            _firePurchaseOnce(orderRef, { order_ref: orderRef, amount: null });
          }
        } catch (error) {
          // Backend confirmed success, show success even if check fails
          setPaymentData({ order_ref: orderRef });
          setStatus("success");
          _firePurchaseOnce(orderRef, { order_ref: orderRef, amount: null });
        }
        return;
      }

      try {
        // Check payment status from backend
        const response = await axios.get(`${API}/payments/check`, {
          params: { ref: orderRef }
        });

        if (response.data.success) {
          setPaymentData(response.data);
          const newStatus = response.data.payment_status === "paid" ? "success" :
                   response.data.payment_status === "failed" ? "failed" : "pending";
          setStatus(newStatus);
          if (newStatus === "success") {
            _firePurchaseOnce(orderRef, response.data);
          }
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Error checking payment:", error);
        setStatus("error");
      }
    };

    // Fire GA4 purchase event only once per order_ref (survives reload via sessionStorage)
    const _firePurchaseOnce = (orderRef, data) => {
      try {
        const key = `ga_purchase_${orderRef}`;
        if (sessionStorage.getItem(key)) return;
        trackPurchase({
          transactionId: data?.order?.id || orderRef,
          total: data?.order?.total ?? data?.amount,
          items: data?.order?.items || [],
        });
        sessionStorage.setItem(key, "1");
      } catch (e) {
        // Non-critical
      }
    };

    checkPaymentStatus();
  }, [location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4" data-testid="payment-return-page">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <img src={LOGO_URL} alt="Granatus" className="h-16 mx-auto mb-4" />
        </CardHeader>
        <CardContent className="space-y-6">
          {status === "loading" && (
            <>
              <div className="w-16 h-16 mx-auto border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-lg">Проверка на плащането...</p>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-green-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                Плащането е успешно!
              </h2>
              <p className="text-muted-foreground">
                Благодарим ви за поръчката. Ще получите потвърждение скоро.
              </p>
              {paymentData && (
                <div className="bg-muted rounded-lg p-4 text-left">
                  <p className="text-sm"><strong>Референция:</strong> {paymentData.order_ref}</p>
                  <p className="text-sm"><strong>Сума:</strong> {paymentData.amount?.toFixed(2)} {paymentData.currency}</p>
                </div>
              )}
            </>
          )}
          
          {status === "failed" && (
            <>
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <X className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                Плащането е неуспешно
              </h2>
              <p className="text-muted-foreground">
                Възникна проблем с плащането. Моля, опитайте отново или изберете плащане в брой.
              </p>
            </>
          )}
          
          {status === "pending" && (
            <>
              <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-10 w-10 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold text-yellow-600" style={{ fontFamily: "'Playfair Display', serif" }}>
                Плащането се обработва
              </h2>
              <p className="text-muted-foreground">
                Вашето плащане се обработва. Ще получите потвърждение скоро.
              </p>
            </>
          )}
          
          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <X className="h-10 w-10 text-gray-600" />
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                Грешка
              </h2>
              <p className="text-muted-foreground">
                Не можахме да проверим статуса на плащането.
              </p>
            </>
          )}
          
          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={() => navigate("/store")}>
              Към магазина
            </Button>
            <Button onClick={() => navigate("/")}>
              Начална страница
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Cancel Page - Customer cancelled payment
const PaymentCancelPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderRef = params.get("ref");

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4" data-testid="payment-cancel-page">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <img src={LOGO_URL} alt="Granatus" className="h-16 mx-auto mb-4" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center">
            <X className="h-10 w-10 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Плащането е отменено
          </h2>
          <p className="text-muted-foreground">
            Плащането беше отменено. Вашата кошница е запазена.
          </p>
          {orderRef && (
            <p className="text-sm text-muted-foreground">Референция: {orderRef}</p>
          )}
          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={() => navigate("/store")}>
              Към магазина
            </Button>
            <Button onClick={() => navigate("/")}>
              Опитай отново
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Terms and Conditions Page
const TermsPage = () => {
  const [terms, setTerms] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const t = translations.terms;

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await axios.get(`${API}/terms`);
        setTerms(response.data.terms || "Общите условия все още не са добавени.");
      } catch (error) {
        console.error("Error fetching terms:", error);
        setTerms("Грешка при зареждане на общите условия.");
      }
      setLoading(false);
    };
    fetchTerms();
  }, []);

  return (
    <div className="pt-20 md:pt-24 min-h-screen bg-muted" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-full"></div>
                <div className="h-4 bg-muted rounded w-5/6"></div>
              </div>
            ) : (
              <div className="prose prose-stone max-w-none">
                <div className="whitespace-pre-wrap text-foreground/80 leading-relaxed">
                  {terms}
                </div>
              </div>
            )}
            <div className="mt-8 pt-8 border-t">
              <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-home-btn">
                <ChevronRight className="h-4 w-4 mr-2 rotate-180" />
                {t.backToHome}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  const t = translations.footer;
  const [socialLinks, setSocialLinks] = useState({ instagram_url: "", facebook_url: "" });
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data) {
          setSocialLinks({
            instagram_url: response.data.instagram_url || "",
            facebook_url: response.data.facebook_url || ""
          });
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="bg-foreground text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Granatus Bakery & Cafe" className="h-20 w-auto mb-4" />
            <p className="text-white/70 max-w-md">{t.description}</p>
            {/* Social Media Links */}
            <div className="flex gap-4 mt-6">
              {socialLinks.instagram_url && (
                <a 
                  href={socialLinks.instagram_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                  data-testid="footer-instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialLinks.facebook_url && (
                <a 
                  href={socialLinks.facebook_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
                  data-testid="footer-facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t.contact}</h4>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4" />ул. Искър 33, София</li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4" />0878787616</li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">{t.hours}</h4>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-center gap-2"><Clock className="h-4 w-4" />Пон, Ср-Нед: 7:00 - 19:00</li>
              <li className="pl-6 text-accent">Вторник: почивен ден</li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">© {new Date().getFullYear()} Granatus Bakery & Cafe. {t.rights}</p>
          <div className="flex gap-6 text-sm">
            <Link to="/terms" className="text-white/50 hover:text-white/80 transition-colors" data-testid="footer-terms-link">
              Общи условия
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App
function App() {
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        console.error("Seed error:", error);
      }
    };
    seedData();

    // Load Google Analytics ID from public settings and bootstrap GA4
    const bootstrapGA = async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        if (data && data.google_analytics_id) {
          initGA(data.google_analytics_id);
          // Track the initial page load once GA is configured
          trackPageView(window.location.pathname + window.location.search, document.title);
        }
      } catch (error) {
        // Silent — analytics is non-critical for app function
        console.error("GA bootstrap error:", error);
      }
    };
    bootstrapGA();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <CartProvider>
          <RouteChangeTracker />
          <Routes>
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/payment/return" element={<PaymentReturnPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/terms" element={<><Navigation /><TermsPage /></>} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="*" element={
              <>
                <Navigation />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/store" element={<StorePage />} />
                  <Route path="/catering" element={<CateringPage />} />
                </Routes>
              </>
            } />
          </Routes>
          <Toaster position="bottom-right" richColors />
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

// Fires a GA4 page_view on every route change in the SPA.
function RouteChangeTracker() {
  const location = useLocation();
  useEffect(() => {
    // Defer to the next tick so document.title reflects the freshly-mounted page
    const id = setTimeout(() => {
      trackPageView(location.pathname + location.search, document.title);
    }, 50);
    return () => clearTimeout(id);
  }, [location]);
  return null;
}

export default App;
